import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  FormGroup,
} from "reactstrap";
import { getSpaSubCategories, saveSpaSubCategory, exportSpaSubCategoriesToExcel } from "../../components/Api/GmsSpaSubCategory";
import { getSpaCategories } from "../../components/Api/GmsSpaCategory";
import { SquarePen, Download } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const defaultForm = {
  subCategory_Id: 0,
  category_Id: null,
  name_En: "",
  name_Ar: "",
  status: true,
  modifiedBy: 1,
  modifiedOn: new Date().toISOString(),
};

const emptyFilter = {
  category_Id: null,
  name_En: "",
  name_Ar: "",
  status: null,
};

export default function SpaSubCategoryPage() {
  // Check permissions first
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsSpaSubCategory');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsSpaSubCategory');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsSpaSubCategory');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsSpaSubCategory');
  const isAdmin = isUserAdmin();

  const [spaSubCategories, setSpaSubCategories] = useState([]);
  const [spaCategories, setSpaCategories] = useState([]);
  const [form, setForm] = useState(() => {
    // Set default status based on permissions
    const defaultStatus = hasDeletePermission ? defaultForm.status : true;
    return { ...defaultForm, status: defaultStatus };
  });
  const [filter, setFilter] = useState(emptyFilter);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("1");
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Session management
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
    };
  };

  const formatDateTime = (dateTimeStr) => {
    try {
      return dateTimeStr.split('T')[0];
    } catch (error) {
      return "N/A";
    }
  };

  const formatFullDateTime = (dateTimeStr) => {
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleString();
    } catch (error) {
      return "N/A";
    }
  };

  useEffect(() => {
    console.log('Component mounted');
    if (hasPermission) {
      console.log('Loading data due to permission');
      loadSpaCategories(); // Load spa categories first
      loadSpaSubCategories(); // Load spa sub categories on component mount
      
      // Set default tab based on permissions
      if (isViewOnly) {
        setActiveTab("2"); // Show list tab for view-only users
      }
    } else {
      console.log('No permission to view page');
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, isViewOnly]);

  useEffect(() => {
    if (hasAppliedFilter) {
      loadSpaSubCategories();
      setHasAppliedFilter(false);
    }
  }, [hasAppliedFilter]);

  const loadSpaCategories = async () => {
    try {
      const data = await getSpaCategories({ status: true }); // Only active categories
      setSpaCategories(data || []);
    } catch (error) {
      console.error("Error loading spa categories:", error);
      Swal.fire("Error", "Failed to load spa categories", "error");
    }
  };

  const loadSpaSubCategories = async () => {
    try {
      setLoading(true);
      const data = await getSpaSubCategories(filter);
      setSpaSubCategories(data || []);
      setTotalCount(data?.length || 0);
    } catch (error) {
      console.error("Error loading spa sub categories:", error);
      Swal.fire("Error", "Failed to load spa sub categories", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaveLoading(true);
      const session = getUserSession();
      
      const saveData = {
        ...form,
        modifiedBy: session.userId,
      };

      await saveSpaSubCategory(saveData, session.userId);
      
      Swal.fire("Success", "Spa Sub Category saved successfully!", "success");
      
      // Reset form and reload data
      setForm(defaultForm);
      setIsEditing(false);
      loadSpaSubCategories();
    } catch (error) {
      console.error("Error saving spa sub category:", error);
      let errorMessage = "Failed to save spa sub category";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEdit = (item) => {
    setForm({
      subCategory_Id: item.subCategory_Id,
      category_Id: item.category_Id,
      name_En: item.name_En,
      name_Ar: item.name_Ar,
      status: item.status,
      modifiedBy: item.modifiedBy,
      modifiedOn: item.modifiedOn,
    });
    setIsEditing(true);
    setActiveTab("1");
  };

  const handleCancel = () => {
    // Set default status based on permissions
    const defaultStatus = hasDeletePermission ? defaultForm.status : true;
    setForm({ ...defaultForm, status: defaultStatus });
    setIsEditing(false);
  };

  const handleApplyFilter = () => {
    setHasAppliedFilter(true);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setHasAppliedFilter(true);
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      // Clean filter - remove empty strings and convert to proper format
      const cleanFilter = {
        ...(filter.category_Id !== null && filter.category_Id !== undefined && { category_Id: filter.category_Id }),
        ...(filter.name_En && filter.name_En.trim() !== '' && { name_En: filter.name_En }),
        ...(filter.name_Ar && filter.name_Ar.trim() !== '' && { name_Ar: filter.name_Ar }),
        ...(filter.status !== null && filter.status !== undefined && { status: filter.status })
      };
      
      await exportSpaSubCategoriesToExcel(cleanFilter);
      Swal.fire("Success", "Spa sub category report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting spa sub categories:", error);
      Swal.fire("Error", "Failed to export spa sub category report", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  // Get category name by ID
  const getCategoryName = (categoryId) => {
    const category = spaCategories.find(cat => cat.category_Id === categoryId);
    return category ? category.name_En : "N/A";
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = spaSubCategories.slice(startIndex, endIndex);

  // Category options for dropdown
  const categoryOptions = useMemo(() => {
    return spaCategories.map(cat => ({
      value: cat.category_Id,
      label: cat.name_En
    }));
  }, [spaCategories]);

  // Filter options for status
  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" }
  ];

  if (!hasPermission) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Spa Sub Category Management" breadcrumbItem="Spa" />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody className="p-0">
                <Nav tabs className="nav-tabs-custom">
                  {hasEditOrHigher && (
                    <NavItem>
                      <NavLink
                        style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "1" ? "bold" : "normal",
                        color: activeTab === "1" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => setActiveTab("1")}
                      >
                        <i className="bx bx-plus-circle"></i>
                        <span>Add Sub Category</span>
                      </NavLink>
                    </NavItem>
                  )}
                  <NavItem>
                    <NavLink
                      style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "2" ? "bold" : "normal",
                        color: activeTab === "2" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => setActiveTab("2")}
                    >
                      <i className="bx bx-list-ul"></i>
                      <span>Sub Categories List</span>
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent activeTab={activeTab} className="p-4">
                    <TabPane tabId="1">
                      <div>
                        <Form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                          <Row>
                            <Col md={6}>
                              <FormGroup className="mb-4">
                                <Label for="category_Id">Category <span className="text-danger">*</span></Label>
                                <Select
                                  value={categoryOptions.find(option => option.value === form.category_Id)}
                                  onChange={(selected) => setForm({ ...form, category_Id: selected?.value || null })}
                                  options={categoryOptions}
                                  placeholder="Select Category"
                                  styles={selectStyles}
                                  isClearable
                                  required
                                />
                              </FormGroup>
                            </Col>
                            <Col md={6}>
                              <FormGroup className="mb-4">
                                <Label for="name_En">Sub Category Name (English) <span className="text-danger">*</span></Label>
                                <Input
                                  id="name_En"
                                  name="name_En"
                                  type="text"
                                  value={form.name_En}
                                  onChange={(e) => setForm({ ...form, name_En: e.target.value })}
                                  placeholder="Enter Sub Category Name in English"
                                  required
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                          <Row>
                            <Col md={6}>
                              <FormGroup className="mb-4">
                                <Label for="name_Ar">Sub Category Name (Arabic)</Label>
                                <Input
                                  id="name_Ar"
                                  name="name_Ar"
                                  type="text"
                                  value={form.name_Ar}
                                  onChange={(e) => setForm({ ...form, name_Ar: e.target.value })}
                                  placeholder="Enter Sub Category Name in Arabic"
                                />
                              </FormGroup>
                            </Col>
                            {hasDeletePermission && (
                              <Col md={6}>
                                <FormGroup className="mb-4">
                                  <div className="form-check form-switch">
                                    <Input
                                      type="checkbox"
                                      className="form-check-input"
                                      id="status"
                                      checked={form.status}
                                      onChange={(e) => setForm({ ...form, status: e.target.checked })}
                                    />
                                    <Label className="form-check-label" for="status">
                                      {form.status ? "Active" : "Inactive"}
                                    </Label>
                                  </div>
                                </FormGroup>
                              </Col>
                            )}
                          </Row>
                          <Row className="mt-4">
                            <Col>
                              <div className="d-flex gap-2">
                                <Button
                                  type="submit"
                                  color="primary"
                                  disabled={saveLoading || !form.category_Id || !form.name_En.trim()}
                                  className="waves-effect waves-light"
                                >
                                  {saveLoading && (
                                    <ClipLoader size={16} color="#ffffff" className="me-2" />
                                  )}
                                  {isEditing ? "Update Sub Category" : "Save Sub Category"}
                                </Button>
                                <Button
                                  type="button"
                                  color="secondary"
                                  onClick={handleCancel}
                                  className="waves-effect waves-light"
                                >
                                  Clear
                                </Button>
                              </div>
                            </Col>
                          </Row>
                        </Form>
                      </div>
                    </TabPane>

                    <TabPane tabId="2">
                      <div>
                        <Card className="mb-4">
                          <CardBody>
                            <div className="d-flex justify-content-between align-items-center">
                              <h5 className="card-title mb-3">Filter Sub Categories</h5>
                              <Button
                                style={{
                                  backgroundColor: '#556ee6',
                                  borderColor: '#556ee6',
                                  color: 'white',
                                  borderRadius: '6px',
                                  padding: '8px 16px',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  border: 'none',
                                  cursor: 'pointer'
                                }}
                                onClick={() => setShowFilters(!showFilters)}
                              >
                                {showFilters ? "Hide Filters" : "Show Filters"}
                              </Button>
                            </div>
                            {showFilters && (
                              <Row className="g-3">
                                <Col md={4}>
                                  <Label for="filterCategory">Category</Label>
                                  <Select
                                    value={categoryOptions.find(option => option.value === filter.category_Id)}
                                    onChange={(selected) => setFilter({ ...filter, category_Id: selected?.value || null })}
                                    options={categoryOptions}
                                    placeholder="Select Category"
                                    styles={selectStyles}
                                    isClearable
                                    isSearchable
                                  />
                                </Col>
                                <Col md={4}>
                                  <Label for="filterNameEn">Sub Category Name (English)</Label>
                                  <Input
                                    id="filterNameEn"
                                    name="filterNameEn"
                                    type="text"
                                    value={filter.name_En}
                                    onChange={(e) => setFilter({ ...filter, name_En: e.target.value })}
                                    placeholder="Search sub category name..."
                                  />
                                </Col>
                                <Col md={4}>
                                  <Label for="filterStatus">Status</Label>
                                  <Select
                                    value={statusOptions.find(option => option.value === filter.status)}
                                    onChange={(selected) => setFilter({ ...filter, status: selected?.value || null })}
                                    options={statusOptions}
                                    placeholder="Select Status"
                                    styles={selectStyles}
                                    isClearable
                                  />
                                </Col>
                                <Col md={12} className="d-flex align-items-end">
                                  <div className="d-flex gap-2">
                                    <Button
                                      color="primary"
                                      onClick={handleApplyFilter}
                                      className="waves-effect waves-light"
                                    >
                                      Apply Filter
                                    </Button>
                                    <Button
                                      color="secondary"
                                      onClick={handleClearFilter}
                                      className="waves-effect waves-light"
                                    >
                                      Clear Filter
                                    </Button>
                                  </div>
                                </Col>
                              </Row>
                            )}
                          </CardBody>
                        </Card>

                        <Card>
                          <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h5 className="card-title mb-0">
                                Sub Category List
                              </h5>
                              <div className="d-flex gap-2">
                                <Button
                                  color="success"
                                  onClick={handleExportExcel}
                                  disabled={loading || totalCount === 0}
                                  className="waves-effect waves-light"
                                >
                                  <Download className="me-1" size={16} />
                                  Export Excel
                                </Button>
                                {hasEditOrHigher && (
                                  <Button
                                    color="primary"
                                    onClick={() => setActiveTab("1")}
                                    className="waves-effect waves-light"
                                  >
                                    <i className="bx bx-plus me-1"></i>
                                    Add New Sub Category
                                  </Button>
                                )}
                              </div>
                            </div>
                            
                            {loading ? (
                              <div className="text-center p-4">
                                <ClipLoader size={50} color="#556ee6" />
                                <p className="mt-2">Loading sub categories...</p>
                              </div>
                            ) : (
                              <>
                                <div className="table-responsive">
                                  <Table className="table table-bordered table-striped table-hover">
                                    <thead className="table-light">
                                      <tr>
                                        <th width="5%">#</th>
                                        <th width="20%">Category Name</th>
                                        <th width="25%">Sub Category Name (En)</th>
                                        <th width="25%">Sub Category Name (Ar)</th>
                                        <th width="15%">Status</th>
                                        <th width="10%">Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {paginatedData.length > 0 ? (
                                        paginatedData.map((item, index) => (
                                          <tr key={item.subCategory_Id}>
                                            <td>{startIndex + index + 1}</td>
                                            <td>{getCategoryName(item.category_Id)}</td>
                                            <td>{item.name_En}</td>
                                            <td>{item.name_Ar || "N/A"}</td>
                                            <td>
                                              <span className={`badge ${item.status ? 'bg-success' : 'bg-danger'}`}>
                                                {item.status ? "Active" : "Inactive"}
                                              </span>
                                            </td>
                                            <td>
                                              {hasEditOrHigher && (
                                                <Button
                                                  color="primary"
                                                  size="sm"
                                                  onClick={() => handleEdit(item)}
                                                  disabled={editLoadingRow === item.subCategory_Id}
                                                  className="waves-effect waves-light"
                                                >
                                                  {editLoadingRow === item.subCategory_Id ? (
                                                    <ClipLoader size={12} color="#ffffff" />
                                                  ) : (
                                                    <SquarePen size={12} />
                                                  )}
                                                </Button>
                                              )}
                                            </td>
                                          </tr>
                                        ))
                                      ) : (
                                        <tr>
                                          <td colSpan="6" className="text-center">
                                            {hasAppliedFilter ? "No sub categories found matching the filter criteria." : "No sub categories available. Please add some sub categories first."}
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </Table>
                                </div>
                                
                                {/* Pagination */}
                                {totalPages > 1 && (
                                  <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div>
                                      <p className="text-muted mb-0">
                                        Showing {startIndex + 1} to {Math.min(endIndex, totalCount)} of {totalCount} entries
                                      </p>
                                    </div>
                                    <div className="d-flex gap-2">
                                      <Button
                                        color="primary"
                                        size="sm"
                                        onClick={() => handlePageChange(page - 1)}
                                        disabled={page === 1}
                                      >
                                        Previous
                                      </Button>
                                      <span className="align-self-center mx-2">
                                        Page {page} of {totalPages}
                                      </span>
                                      <Button
                                        color="primary"
                                        size="sm"
                                        onClick={() => handlePageChange(page + 1)}
                                        disabled={page === totalPages}
                                      >
                                        Next
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </CardBody>
                        </Card>
                      </div>
                    </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
